import { Boolean, Literal, Record as RRecord, Static, Union } from "runtypes";

import { ProjectLanguage } from "./enums.js";
import { assertNever } from "./errors.js";
import { AppSessionId } from "./idTypeBrands.js";
import { KernelSize } from "./kernelConfigTypes.js";
import { KernelImage } from "./typeBrands.js";
import { uuid } from "./uuid.js";

export const KernelAppLabelLiteral = Union(
  Literal("hex-python-kernel"),
  Literal("hex-r-kernel"),
);
export type KernelAppLabelLiteral = Static<typeof KernelAppLabelLiteral>;

export const getKernelName = ({
  language,
  prefix,
}: {
  language: ProjectLanguage;
  prefix: string;
}): string => {
  switch (language) {
    case ProjectLanguage.R:
      return `${prefix}-r-kernel`;
    case ProjectLanguage.PYTHON:
      return `${prefix}-python-kernel`;
    default:
      assertNever(language, language);
  }
};

export const SidecarContainerNames = Union(
  Literal("hex-kernel-data-service"),
  Literal("hex-minerva"),
);
export type SidecarContainerNames = Static<typeof SidecarContainerNames>;

export const getKernelApp = ({
  language,
}: {
  language: ProjectLanguage;
}): KernelAppLabelLiteral =>
  KernelAppLabelLiteral.check(getKernelName({ language, prefix: "hex" }));

export const getFullKernelName = ({
  kernelImage,
  kernelSize,
  language,
  prefix,
}: {
  language: ProjectLanguage;
  prefix: string;
  kernelImage: KernelImage;
  kernelSize: KernelSize;
}): string => {
  const suffix = uuid().replace(/-/gi, "");
  return `${getKernelName({
    language,
    prefix,
  })}-${kernelImage}-${kernelSize}-${suffix}`;
};

export const getKernelGenerateName = ({
  kernelImage,
  kernelSize,
  language,
  prefix,
}: {
  language: ProjectLanguage;
  prefix: string;
  kernelImage: KernelImage;
  kernelSize: KernelSize;
}): string => {
  return `${getKernelName({
    language,
    prefix,
  })}-${kernelImage}-${kernelSize}-`;
};

// prewarm pool pods come from the `<release>-prewarmed-kernels-...` Deployment,
// so this substring is in their name. on-demand pods are named via
// getFullKernelName and never contain it. the name is immutable, so unlike the
// `prewarmed` label (which claim flips to "false") it survives claiming.
export const PREWARM_POOL_POD_NAME_MARKER = "prewarmed-kernels";

export const isPrewarmPoolPodName = (podName: string): boolean =>
  podName.includes(PREWARM_POOL_POD_NAME_MARKER);

export function createPodHostnameAndSubdomain({
  appSessionId,
  releaseName,
}: {
  appSessionId: AppSessionId;
  releaseName: string;
}): { hostname: string; subdomain: string } {
  return {
    hostname: `hex-kernel-for-${appSessionId}`,
    subdomain: `${releaseName}-kernel`,
  };
}

export const KernelManagementCPMigrationState = RRecord({
  compareBackup: Boolean,
  primary: Union(Literal("K8s"), Literal("CP")),
});

export type KernelManagementCPMigrationState = Static<
  typeof KernelManagementCPMigrationState
>;

export const DefaultKernelManagementCPMigrationState: KernelManagementCPMigrationState =
  { primary: "CP", compareBackup: false };
